'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { registerAuthCleanup, cleanupLegacyAuthTokens } from '@/lib/auth/session-cleanup';
import { PhoneInput } from './PhoneInput';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

type LoginStep = 'input' | 'sent' | 'add-phone';

interface LoginFormProps {
  initialSignUpMode?: boolean;
}

export default function LoginForm({ initialSignUpMode = false }: LoginFormProps) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [loginStep, setLoginStep] = useState<LoginStep>('input');
  const [countdown, setCountdown] = useState(0);
  const [isSignUp, setIsSignUp] = useState(initialSignUpMode);
  const { signInWithPhone, refreshSession, isLoading, isAuthenticated, profile } = useSupabaseAuth();
  const [needsPhoneCollection, setNeedsPhoneCollection] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = searchParams.get('redirect') || searchParams.get('next') || '/dashboard/moments';

  // Run auth cleanup on component mount
  useEffect(() => {
    registerAuthCleanup();
    cleanupLegacyAuthTokens();
  }, []);

  // Initialize from initialSignUpMode prop
  useEffect(() => {
    console.log('initialSignUpMode prop is', initialSignUpMode, 'updating state');
    setIsSignUp(initialSignUpMode);
  }, [initialSignUpMode]);

  // Check URL parameters for error
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');

    if (errorParam) {
      setMessage({
        type: 'error',
        text: messageParam
          ? decodeURIComponent(messageParam)
          : 'Authentication failed. Please try again.'
      });
    }
  }, [searchParams]);

  // Run countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle login redirect after successful authentication
  useEffect(() => {
    const handleAuth = async () => {
      if (isAuthenticated && profile) {
        console.log('[LoginForm] User is authenticated, checking for phone...');

        // Check if user needs to add phone number
        if (!profile.phone && !needsPhoneCollection) {
          console.log('[LoginForm] User needs to add phone number');
          setNeedsPhoneCollection(true);
          setLoginStep('add-phone');
          return;
        }

        // If we've already collected phone or user has one, proceed with redirect
        if (profile.phone || needsPhoneCollection) {
          console.log('[LoginForm] Proceeding with redirect...');

          try {
            await refreshSession();
            console.log('[LoginForm] Session refreshed successfully before redirect');
          } catch (refreshError) {
            console.error('[LoginForm] Error refreshing session:', refreshError);
          }

          const redirectUrl = new URL(redirectPath, window.location.origin);
          const timestamp = Date.now();
          redirectUrl.searchParams.set('ts', timestamp.toString());

          console.log(`[LoginForm] Redirecting to ${redirectUrl.toString()}`);

          if (redirectUrl.origin === window.location.origin) {
            router.push(redirectUrl.toString());
          } else {
            window.location.href = redirectUrl.toString();
          }
        }
      }
    };

    handleAuth();
  }, [isAuthenticated, profile, redirectPath, refreshSession, router, needsPhoneCollection]);

  // Handle form submission for phone
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!phone) {
      setMessage({ type: 'error', text: 'Please enter your phone number' });
      return;
    }

    // Validate phone number
    if (!isValidPhoneNumber(phone)) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number' });
      return;
    }

    // Email is always required for notifications
    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email for notifications' });
      return;
    }

    try {
      const result = await signInWithPhone(phone, email || undefined);

      if (result.success) {
        if (result.fallbackUsed) {
          setMessage({
            type: 'warning',
            text: 'SMS unavailable. We sent a verification link to your email instead. Check your inbox!'
          });
        } else {
          setMessage({
            type: 'success',
            text: 'Verification code sent! Check your messages.'
          });
        }
        setLoginStep('sent');
        setCountdown(60);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to send verification code. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
      console.error('Login error:', error);
    }
  };

  const resendCode = async () => {
    if (countdown > 0) return;

    try {
      const result = await signInWithPhone(phone, email || undefined);

      if (result.success) {
        if (result.fallbackUsed) {
          setMessage({
            type: 'warning',
            text: 'SMS unavailable. New verification link sent to your email!'
          });
        } else {
          setMessage({
            type: 'success',
            text: 'New verification code sent! Check your messages.'
          });
        }
        setCountdown(60);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to resend code. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
      console.error('Error resending code:', error);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
  };

  // Handle saving phone number for existing users
  const handleSavePhone = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!phone) {
      setMessage({ type: 'error', text: 'Please enter your phone number' });
      return;
    }

    if (!isValidPhoneNumber(phone)) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number' });
      return;
    }

    try {
      // Save phone number to user profile
      const response = await fetch('/api/user/update-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || 'Failed to save phone number' });
        return;
      }

      setMessage({ type: 'success', text: 'Phone number saved!' });
      setNeedsPhoneCollection(true); // Mark as collected

      // Trigger redirect by refreshing profile
      await refreshSession();
    } catch (error) {
      console.error('Error saving phone:', error);
      setMessage({ type: 'error', text: 'Failed to save phone number' });
    }
  };

  // Skip adding phone number
  const handleSkipPhone = () => {
    setNeedsPhoneCollection(true); // Mark as handled (skipped)
  };

  return (
    <div className="rounded-lg bg-white dark:bg-gray-800 p-8 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {isSignUp ? 'Create an account' : 'Sign in to your account'}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {isSignUp
            ? 'Join the community and start sharing'
            : 'Access your dashboard and conversations'}
        </p>
      </div>

      {message && (
        <div className={`rounded-md p-4 mb-4 ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-100'
            : message.type === 'warning'
            ? 'bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100'
            : 'bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-100'
        }`}>
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      {loginStep === 'input' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone number
            </label>
            <PhoneInput
              value={phone}
              onChange={(value) => setPhone(value || '')}
              disabled={isLoading}
              required
              autoFocus
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                       text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Used for notifications and SMS fallback
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md
                       shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : (isSignUp ? 'Sign up' : 'Sign in')}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={toggleMode}
              className="text-sm text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 focus:outline-none"
            >
              {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
            </button>
          </div>
        </form>
      )}

      {loginStep === 'sent' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Check your messages</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              We've sent a verification code to {phone}
              {email && <><br/>Fallback email: {email}</>}
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Enter the code from your SMS to complete sign in
            </p>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={resendCode}
              disabled={countdown > 0 || isLoading}
              className="text-sm text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 focus:outline-none
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-orange-600 dark:disabled:hover:text-orange-400"
            >
              {countdown > 0 ? `Resend code (${countdown}s)` : 'Resend code'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setLoginStep('input');
                setMessage(null);
              }}
              className="text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 focus:outline-none"
            >
              Use a different number
            </button>
          </div>
        </div>
      )}

      {loginStep === 'add-phone' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 mb-4">
              <svg className="w-6 h-6 text-orange-600 dark:text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Add your phone number</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Add your phone number for faster SMS verification on future logins
            </p>
          </div>

          <form onSubmit={handleSavePhone} className="space-y-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone number
              </label>
              <PhoneInput
                value={phone}
                onChange={(value) => setPhone(value || '')}
                disabled={isLoading}
                required
                autoFocus
                placeholder="Enter your phone number"
              />
            </div>

            {message && (
              <div className={`rounded-md p-4 ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-100'
                  : message.type === 'warning'
                  ? 'bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100'
                  : 'bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-100'
              }`}>
                <p className="text-sm">{message.text}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md
                       shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : 'Save phone number'}
            </button>

            <button
              type="button"
              onClick={handleSkipPhone}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md
                       shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800
                       hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              Skip for now
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
